<template>
  <div
    class="sequence-card"
    @mouseover="mouseHover = true"
    @mouseleave="mouseHover = false"
  >

    <div class="sequence-card__header">
      <div class="sequence-card__header-row">
        <div v-if="shouldShowSelector" class="sequence-card__checkbox" @click.stop="">
          <v-checkbox
            v-model="isSelected"
            :hide-details="true"
            density="compact"
            @update:model-value="toggleSelection"
          />
        </div>
        <span class="sequence-card__image-count">Images: {{ sequence.no_of_images ?? 'â€”' }}</span>
        <div class="sequence-card__menu" @click.stop="">
          <BaseMenu
            :placement="'bottom-end'"
            @closed="show_menu = false"
          >
            <IconButton
              v-model="show_menu"
              :icon="'more_vert'"
              :width="24"
              :height="24"
            />
            <template #menu="{ closeMenu }">
              <ul class="sequence-card__menu-list">
                <li>
                  <BaseMenuButton @click.stop="showInfoDialog = true, closeMenu()">
                    <SVGIcon :iconName="'info'" :width="'22px'" :height="'22px'" />
                    <span>Info</span>
                  </BaseMenuButton>
                </li>
                <li>
                  <BaseMenuButton @click.stop="openDescriptionEditor(), closeMenu()">
                    <SVGIcon :iconName="'edit'" :width="'22px'" :height="'22px'" />
                    <span>Edit Description</span>
                  </BaseMenuButton>
                </li>
                <li>
                  <BaseMenuButton @click.stop="$emit('download', sequence), closeMenu()">
                    <SVGIcon :iconName="'download'" :width="'22px'" :height="'22px'" />
                    <span>Download Sequence</span>
                  </BaseMenuButton>
                </li>
                <li>
                  <BaseMenuButton @click.stop="$emit('delete', sequence), closeMenu()">
                    <SVGIcon :iconName="'delete'" :width="'22px'" :height="'22px'" />
                    <span>Delete Sequence</span>
                  </BaseMenuButton>
                </li>
              </ul>
            </template>
          </BaseMenu>
        </div>
      </div>
    </div>
    <div class="sequence-card__image-wrapper" v-tooltip:top="'Click to go to editor'" @click="handleCardClick">
      <ImageCanvas
        v-if="firstImageId"
        :imageId="firstImageId"
        :imageQuality="'thumbnail'"
      />
    </div>
    <div class="sequence-card__details">
      <div class="sequence-card__kv">
        <span class="sequence-card__kv-key">Name</span>
        <span class="sequence-card__kv-value" :title="sequence.name">{{ sequence.name }}</span>
      </div>
      <div class="sequence-card__kv">
        <span class="sequence-card__kv-key">UUID</span>
        <span class="sequence-card__kv-value" :title="sequence.uuid">{{ sequence.uuid }}</span>
      </div>
      <div v-if="sequence.date" class="sequence-card__kv">
        <span class="sequence-card__kv-key">Date</span>
        <span class="sequence-card__kv-value" :title="sequence.date">{{ formatDate(sequence.date) }}</span>
      </div>
      <DescriptionField
        ref="descriptionFieldRef"
        :text="sequence.description"
        :clickable="true"
        :data="sequence"
        @description-changed="handleDescriptionChanged"
      />
    </div>
  </div>

  <SequenceInfoDialog
    v-model="showInfoDialog"
    :sequence="sequence"
    :annotation-sets="annotationSets"
  />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ImageCanvas from '@/components/ImageCanvas.vue';
import IconButton from '@/components/IconButton.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import BaseMenu from '@/components/BaseMenu.vue';
import BaseMenuButton from '@/components/BaseMenuButton.vue';
import DescriptionField from '@/components/Cards/DescriptionField.vue';
import SequenceInfoDialog from '@/components/DatasetComponent/GalleryComponent/SequenceInfoDialog.vue';
import { goto_dataset_gallery_viewer } from '@/assets/js/links.js';

const props = defineProps({
  sequence: {
    type: Object,
    default: () => ({}),
  },
  datasetId: {
    type: [String, Number],
    default: null,
  },
  shouldShowSelector: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  annotationDisplaySettings: {
    type: Object,
    default: null,
  },
  annotationSets: {
    type: Array,
    default: () => [],
  },
  imageDisplaySettings: {
    type: Array,
    default: () => [],
  },
  filterParams: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['grid-item-clicked', 'update:selected', 'download', 'delete', 'edit-description']);

const mouseHover = ref(false);
const show_menu = ref(false);
const isSelected = ref(props.selected);
const descriptionFieldRef = ref(null);
const showInfoDialog = ref(false);

// Use image_id directly from sequence
const firstImageId = computed(() => {
  return props.sequence?.image_id || null;
});

const toggleSelection = () => {
  emit('update:selected', !props.selected);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const handleDescriptionChanged = (description) => {
  emit('edit-description', {
    sequenceId: props.sequence.id,
    description,
  });
};

const openDescriptionEditor = () => {
  descriptionFieldRef.value?.openEditDialog?.();
};

const handleCardClick = () => {
  const imageId = props.sequence?.image_id || null;
  const filters = {};
  if (props.annotationDisplaySettings) {
    filters.annotationDisplaySettings = props.annotationDisplaySettings;
  }
  if (props.imageDisplaySettings?.length > 0) {
    filters.imageDisplaySettings = props.imageDisplaySettings;
    filters.showSequences = props.imageDisplaySettings.includes('showSequences');
  }
  if (props.filterParams) {
    filters.filterParams = props.filterParams;
  }
  goto_dataset_gallery_viewer(props.datasetId, null, imageId, filters);
};

// Watch selected prop
watch(() => props.selected, (newVal) => {
  isSelected.value = newVal;
});
</script>

<style lang="scss" scoped>
.sequence-card {
  flex: 0 0 300px;
  width: 300px;
  height: 370px;
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  border-radius: 0;
  overflow: hidden;
  margin: 8px;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  transition: box-shadow 0.2s ease;
  @include themify() {
    background: themed('image-background-fill');
    color: themed('text-color');
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    @include themify() {
      outline: 1px solid themed('card-list-hover-outline');
    }
  }

  &__header {
    display: flex;
    flex-direction: row;
    padding: 6px 8px;
    gap: 6px;
    height: 40px;
    flex: 0 0 40px;
    align-items: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    @include themify() {
      background: themed('image-details-background-fill');
    }
  }

  &__header-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }

  &__image-count {
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  &__checkbox {
    flex: 0 0 auto;
    height: 30px;
    overflow: hidden;

    :deep(.v-input) {
      margin-top: -5px;
      margin-left: -1px;
    }
  }

  &__type-label {
    flex: 1;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  &__name {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  }

  &__menu {
    flex: 0 0 auto;
    margin-left: auto;
  }

  &__menu-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__image-wrapper {
    position: relative;
    height: 200px;
    flex: 0 0 200px;
    overflow: hidden;
    cursor: pointer;

    :deep(.image-canvas) {
      height: 100%;
      width: 100%;
    }

    :deep(.image-canvas__image) {
      height: 100%;
      width: 100%;
      max-width: none;
      object-fit: cover;
    }
  }

  &__video-icon {
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.65);
    border-radius: 100%;
    box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.5);
    color: var(--icon-color-primary);
  }

  &__details {
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 4px;
    flex: 1;
    height: 130px;
    overflow: hidden;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    @include themify() {
      background: themed('image-details-background-fill');
    }
  }

  &__kv {
    display: flex;
    gap: 8px;
    align-items: baseline;
    min-width: 0;
  }

  &__kv-key {
    flex: 0 0 50px;
    font-size: 10px;
    font-weight: 700;
    text-transform: none;
    white-space: nowrap;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  &__kv-value {
    flex: 1;
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &__description {
    flex: 1;
    white-space: normal;
    font-style: italic;
    color: #5a7a94;
    font-size: 11px;
    line-height: 1.4;
    word-wrap: break-word;
    border-top: 1px solid #d0d0d0;
    margin-top: 4px;
    padding-top: 4px;
  }
}

.edit-textarea {
  width: 100%;
  min-height: 120px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
  font-size: 13px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.1);
  }
}

.edit-dialog__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.edit-dialog__buttons {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.button-save {
  background: var(--color-primary);
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    opacity: 0.9;
  }
}

.button-cancel {
  background: #f0f0f0;
  color: #333;
  padding: 8px 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background: #e0e0e0;
  }
}
</style>

